import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Cropper, ImageRestriction, CropperFade } from "react-advanced-cropper";
import {
  stencilConstraints,
  defaultSize,
  transformImage,
  resizeCoordinates,
  fitStencilToImage,
  zoomStencil,
} from "advanced-cropper/showcase/mobile";
import "react-advanced-cropper/dist/style.css";
import CropperWrapper from "./CropperWrapper";
import "./AdvancedCropModal.css";

// Note: The transformCropCoordinatesForRotation function has been removed
// because crop coordinates are always stored in the unrotated coordinate system
// and should be used directly without transformation

const AdvancedCropModal = ({
  isOpen,
  onClose,
  imageSrc,
  imageId,
  onCropConfirm,
  currentCropData = null,
  originalImageData = null,
  currentRotation = 0,
  currentFlipHorizontal = false,
  currentFlipVertical = false,
  currentDisplayWidth = null, // Add prop to know the current display dimensions in canvas
  currentDisplayHeight = null, // Add prop to know the current display dimensions in canvas
  onModalRootRef = () => {}, // Callback to pass modal root element so document listener can allow only modal events
}) => {
  const cropperRef = useRef(null);
  const modalRootRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [changed, setChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rotatedImageSrc, setRotatedImageSrc] = useState(null);
  const [cropperKey, setCropperKey] = useState(0); // Key to force cropper remount
  const [isCompleteReset, setIsCompleteReset] = useState(false); // Flag for complete reset mode

  // Initialize crop data when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setRotatedImageSrc(null); // Reset rotated image
      // Ensure subsequent openings respect saved crop data
      setIsCompleteReset(false);
    }
  }, [isOpen]);

  // No need for rotated image source - using cropper's built-in rotation

  // Set rotation, flip, and coordinates after image loads
  useEffect(() => {
    if (
      isOpen &&
      !isLoading &&
      cropperRef.current &&
      cropperRef.current.isLoaded()
    ) {
      // Set the initial rotation if currentRotation is not 0
      if (currentRotation !== 0) {
        try {
          cropperRef.current.rotate(currentRotation);
        } catch (error) { }
      }

      // Set the initial flip if we have flip data
      // Apply flip with a slight delay to ensure cropper is ready
      if (currentFlipHorizontal || currentFlipVertical) {
        setTimeout(() => {
          if (cropperRef.current && cropperRef.current.isLoaded()) {
            try {
              cropperRef.current.flipImage(
                currentFlipHorizontal,
                currentFlipVertical,
                {
                  normalize: false,
                }
              );
            } catch (error) { }
          }
        }, 100);
      }

      // Set coordinates if we have crop data
      if (currentCropData) {
        // Get the actual image dimensions from the cropper
        const image = cropperRef.current.getImage();
        const imageWidth = image?.naturalWidth || 1;
        const imageHeight = image?.naturalHeight || 1;

        // Handle coordinates based on whether this is a rotated image with existing crop data
        let coordinatesToSet;

        if (currentCropData && currentCropData.left !== undefined) {
          // CRITICAL FIX: Always use stored coordinates directly without transformation
          // The crop coordinates are ALWAYS stored in the unrotated coordinate system
          // The cropper handles rotation via transforms, so coordinates should be used as-is

          coordinatesToSet = {
            left: Math.max(0, Math.min(1, currentCropData.left)),
            top: Math.max(0, Math.min(1, currentCropData.top)),
            width: Math.max(0.01, Math.min(1, currentCropData.width)),
            height: Math.max(0.01, Math.min(1, currentCropData.height)),
          };

          // Ensure coordinates don't exceed image bounds
          coordinatesToSet.width = Math.min(
            coordinatesToSet.width,
            1 - coordinatesToSet.left
          );
          coordinatesToSet.height = Math.min(
            coordinatesToSet.height,
            1 - coordinatesToSet.top
          );
        } else {
          // No crop data available, use a centered crop area
          coordinatesToSet = {
            left: 0.2,
            top: 0.2,
            width: 0.6,
            height: 0.6,
          };
        }

        // Ensure coordinates don't exceed image bounds
        coordinatesToSet.width = Math.min(
          coordinatesToSet.width,
          1 - coordinatesToSet.left
        );
        coordinatesToSet.height = Math.min(
          coordinatesToSet.height,
          1 - coordinatesToSet.top
        );

        // Set coordinates with a delay to ensure cropper is ready
        setTimeout(() => {
          if (cropperRef.current && cropperRef.current.isLoaded()) {
            try {
              cropperRef.current.setCoordinates(coordinatesToSet);

              // Verify coordinates were set correctly
              const currentCoords = cropperRef.current.getCoordinates();
            } catch (error) { }
          }
        }, 200);
      } else if (currentRotation !== 0) {
        // For rotated images without crop data, show full image area

        setTimeout(() => {
          if (cropperRef.current && cropperRef.current.isLoaded()) {
            try {
              cropperRef.current.setCoordinates({
                left: 0,
                top: 0,
                width: 1,
                height: 1,
              });
            } catch (error) { }
          }
        }, 200);
      }
    }
  }, [
    isOpen,
    isLoading,
    currentCropData,
    currentRotation,
    currentFlipHorizontal,
    currentFlipVertical,
  ]);

  // Log the original image data when modal opens for debugging
  useEffect(() => {
    if (isOpen) {
      // Additional debugging for crop data
      if (currentCropData) {
      }
    }
  }, [
    isOpen,
    originalImageData,
    currentCropData,
    imageSrc,
    imageId,
    currentRotation,
  ]);

  // SIMPLIFIED: Let cropper handle rotation through the rotated image source
  // No need for complex rotation handling since we're using rotatedImageSrc

  const handleCropChange = useCallback((cropper) => {
    // Check if the cropper state has changed from default
    const defaultState = cropper.getDefaultState();
    const currentState = cropper.getState();
    setChanged(JSON.stringify(currentState) !== JSON.stringify(defaultState));

    // Debug: Log current coordinates when they change
    const coords = cropper.getCoordinates();

    // Log coordinate format for debugging
    if (coords) {
      const isNormalized =
        coords.left <= 1 &&
        coords.top <= 1 &&
        coords.width <= 1 &&
        coords.height <= 1;

      // Additional debugging for crop box visibility
      const image = cropper.getImage();
      if (image) {
      }
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);

    // Reset the complete reset flag after cropper loads
    setIsCompleteReset(false);

    // Check the initial transforms after image loads
    setTimeout(() => {
      if (cropperRef.current && cropperRef.current.isLoaded()) {
        const transforms = cropperRef.current.getTransforms();

        if (transforms?.rotate !== currentRotation && currentRotation !== 0) {
          // Apply rotation if needed
          cropperRef.current.rotate(currentRotation);
        }

        // Apply flip if needed
        const needsFlipHorizontal =
          transforms?.flip?.horizontal !== currentFlipHorizontal &&
          currentFlipHorizontal;
        const needsFlipVertical =
          transforms?.flip?.vertical !== currentFlipVertical &&
          currentFlipVertical;

        if (needsFlipHorizontal || needsFlipVertical) {
          cropperRef.current.flipImage(
            currentFlipHorizontal,
            currentFlipVertical,
            {
              normalize: false,
            }
          );
        }
      }
    }, 100);
  }, [currentRotation, currentFlipHorizontal, currentFlipVertical]);

  const handleReset = () => {
    if (!cropperRef.current || !cropperRef.current.isLoaded()) {
      return;
    }

    try {
      // Use the built-in reset method to reset everything to default
      // This resets coordinates, zoom, position, and visible area
      cropperRef.current.reset();

      // Reset aspect ratio
      setAspectRatio(null);

      // Reset changed state
      setChanged(false);
    } catch (error) {
      console.error("Error resetting cropper:", error);
    }
  };

  const handleCompleteReset = () => {
    try {
      // Reset to original image - exactly like first opening with no crop data
      // Strategy: Set flag and remount cropper without default coordinates/transforms

      // 1. Reset aspect ratio
      setAspectRatio(null);

      // 2. Reset changed state
      setChanged(false);

      // 3. Set complete reset flag (this prevents passing defaultCoordinates and defaultTransforms)
      setIsCompleteReset(true);

      // 4. Force cropper to remount by changing key
      setCropperKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error performing complete reset:", error);
    }
  };

  const handleCropConfirm = async () => {
    if (!cropperRef.current) {
      alert("Crop tool is not ready. Please try again.");
      return;
    }

    setIsProcessing(true);
    try {
      // Check if cropper is loaded
      if (!cropperRef.current.isLoaded()) {
        throw new Error(
          "Cropper is not loaded yet. Please wait for the image to load."
        );
      }

      // Get the original image for reference
      const trueOriginalImage =
        originalImageData || cropperRef.current.getImage();
      if (!trueOriginalImage) {
        throw new Error("Original image is not available for cropping.");
      }

      let finalCanvas;
      let cropPixelData = null;

      // Get the cropper's canvas - it already contains the rotated and cropped image
      const canvas = cropperRef.current.getCanvas();
      if (!canvas) {
        throw new Error("Cropper getCanvas returned null");
      }

      // Get the current transforms to check if there's rotation or flip
      const transforms = cropperRef.current.getTransforms();
      const hasRotation =
        transforms && transforms.rotate && transforms.rotate !== 0;
      const hasFlip =
        transforms &&
        transforms.flip &&
        (transforms.flip.horizontal || transforms.flip.vertical);

      // CRITICAL FIX: Always use the cropper's canvas for consistency
      // The cropper handles both rotation, flipping, and cropping correctly
      finalCanvas = canvas;

      // Get the crop coordinates to store them for future crop modal openings
      const coordinates = cropperRef.current.getCoordinates();
      if (coordinates) {
        // Check if coordinates are normalized (0-1 range) or in pixels
        const isNormalized =
          coordinates.left <= 1 &&
          coordinates.top <= 1 &&
          coordinates.width <= 1 &&
          coordinates.height <= 1;

        // Store the crop coordinates (always in normalized format)
        // The react-advanced-cropper library handles coordinate transformations internally
        // so we store coordinates as-is and rely on the library to apply them correctly
        // when rotation/flip transforms are present
        cropPixelData = {
          left: coordinates.left,
          top: coordinates.top,
          width: coordinates.width,
          height: coordinates.height,
        };
      }

      // Validate canvas has content
      if (finalCanvas.width === 0 || finalCanvas.height === 0) {
        throw new Error(
          `Canvas has invalid dimensions: ${finalCanvas.width}x${finalCanvas.height}`
        );
      }

      // Convert final canvas to blob with high quality

      const blob = await new Promise((resolve, reject) => {
        try {
          finalCanvas.toBlob(
            (result) => {
              if (result) {
                resolve(result);
              } else {
                reject(new Error("Canvas.toBlob returned null"));
              }
            },
            "image/jpeg",
            0.9 // High quality to preserve details
          );
        } catch (error) {
          reject(new Error(`Canvas.toBlob failed: ${error.message}`));
        }
      });

      if (!blob) {
        throw new Error("Canvas blob creation returned null");
      }

      // Create new image from blob

      const croppedImageUrl = URL.createObjectURL(blob);
      const croppedImage = new Image();

      // Wait for the image to fully load
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Image loading timeout"));
        }, 10000); // 10 second timeout

        croppedImage.onload = () => {
          clearTimeout(timeout);

          resolve();
        };

        croppedImage.onerror = (error) => {
          clearTimeout(timeout);

          reject(
            new Error(
              `Image loading failed: ${error.message || "Unknown error"}`
            )
          );
        };

        croppedImage.src = croppedImageUrl;
      });

      // Call the parent callback with the cropped image and crop data

      onCropConfirm({
        croppedImage,
        croppedImageUrl,
        cropData: cropPixelData,
        originalImageData: trueOriginalImage,
        imageId,
        rotationAngle: transforms?.rotate || 0, // Pass the current rotation angle
        isRotatedImage: hasRotation || hasFlip, // Flag to indicate this is a transformed image (rotated or flipped)
        originalRotation: currentRotation || transforms?.rotate || 0, // Pass the original rotation for reference
        flipHorizontal: transforms?.flip?.horizontal || false, // Pass flip transforms
        flipVertical: transforms?.flip?.vertical || false, // Pass flip transforms
        canvasWidth: finalCanvas.width, // Pass the actual canvas dimensions
        canvasHeight: finalCanvas.height, // Pass the actual canvas dimensions
      });

      onClose();
    } catch (error) {
      alert(`Failed to crop image: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetAspectRatio = (ratio) => {
    setAspectRatio(ratio);
    if (cropperRef.current) {
      const coordinates = cropperRef.current.getCoordinates();
      if (coordinates && ratio) {
        // Calculate new dimensions maintaining the aspect ratio
        const currentWidth = coordinates.width;
        const currentHeight = coordinates.height;

        let newWidth = currentWidth;
        let newHeight = currentHeight;

        // Adjust dimensions to match the aspect ratio
        if (currentWidth / currentHeight > ratio) {
          // Current is wider than desired ratio, adjust width
          newWidth = currentHeight * ratio;
        } else {
          // Current is taller than desired ratio, adjust height
          newHeight = currentWidth / ratio;
        }

        // Center the new crop area
        const newLeft = Math.max(
          0,
          Math.min(
            1 - newWidth,
            coordinates.left + (currentWidth - newWidth) / 2
          )
        );
        const newTop = Math.max(
          0,
          Math.min(
            1 - newHeight,
            coordinates.top + (currentHeight - newHeight) / 2
          )
        );

        cropperRef.current.setCoordinates({
          left: newLeft,
          top: newTop,
          width: newWidth,
          height: newHeight,
        });
      }
    }
  };

  if (!isOpen) return null;

  const setModalRootRef = (el) => {
    modalRootRef.current = el;
    onModalRootRef(el);
  };

  const modalContent = (
    <div
      className="advanced-crop-modal-overlay"
      style={{ zIndex: 10001 }}
      ref={setModalRootRef}
    >
      <div
        className="advanced-crop-modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="advanced-crop-modal-header">
          <button
            className="close-button"
            onClick={onClose}
            disabled={isProcessing}
            aria-label="Close"
            title="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="advanced-crop-modal-content">
          <div className="cropper-container">
            <Cropper
              key={cropperKey}
              ref={cropperRef}
              src={imageSrc}
              className="telegram-cropper"
              defaultCoordinates={
                !isCompleteReset && currentCropData
                  ? {
                    left: currentCropData.left,
                    top: currentCropData.top,
                    width: currentCropData.width,
                    height: currentCropData.height,
                  }
                  : undefined
              }
              defaultTransforms={
                !isCompleteReset &&
                  (currentRotation !== 0 ||
                    currentFlipHorizontal ||
                    currentFlipVertical)
                  ? {
                    rotate: currentRotation,
                    flip: {
                      horizontal: currentFlipHorizontal,
                      vertical: currentFlipVertical,
                    },
                  }
                  : undefined
              }
              stencilProps={{
                aspectRatio: aspectRatio || undefined,
                movable: true,
                resizable: true,
                grid: true,
              }}
              stencilConstraints={stencilConstraints}
              backgroundProps={{
                imageProps: {
                  crossOrigin: "anonymous",
                  onLoad: handleImageLoad,
                },
              }}
              imageRestriction={ImageRestriction.none}
              postProcess={[fitStencilToImage, zoomStencil]}
              defaultSize={defaultSize}
              transformImageAlgorithm={transformImage}
              resizeCoordinatesAlgorithm={resizeCoordinates}
              onChange={handleCropChange}
              transitions={true}
              wrapperComponent={CropperWrapper}
              wrapperProps={{
                navigation: true,
                navigationProps: {
                  rotation: true,
                },
              }}
            />
          </div>

          <div className="crop-controls">
            {/* <div className="control-group">
              <h4>Aspect Ratio</h4>
              <div className="aspect-ratio-buttons">
                <button
                  onClick={() => handleSetAspectRatio(null)}
                  className={aspectRatio === null ? "active" : ""}
                  disabled={isProcessing}
                >
                  Free
                </button>
                <button
                  onClick={() => handleSetAspectRatio(1)}
                  className={aspectRatio === 1 ? "active" : ""}
                  disabled={isProcessing}
                >
                  1:1
                </button>
                <button
                  onClick={() => handleSetAspectRatio(16 / 9)}
                  className={aspectRatio === 16 / 9 ? "active" : ""}
                  disabled={isProcessing}
                >
                  16:9
                </button>
                <button
                  onClick={() => handleSetAspectRatio(4 / 3)}
                  className={aspectRatio === 4 / 3 ? "active" : ""}
                  disabled={isProcessing}
                >
                  4:3
                </button>
                <button
                  onClick={() => handleSetAspectRatio(3 / 2)}
                  className={aspectRatio === 3 / 2 ? "active" : ""}
                  disabled={isProcessing}
                >
                  3:2
                </button>
              </div>
            </div> */}

            <div className="control-group">
              {/* <h4>Actions</h4> */}
              <div className="action-buttons-crop-modal">
                {/* <button
                  onClick={handleReset}
                  disabled={isProcessing}
                  className="reset-button"
                  title="Reset to default state"
                >
                  Reset
                </button> */}
                <button
                  onClick={handleCropConfirm}
                  disabled={isProcessing}
                  className="crop-button"
                >
                  {isProcessing ? "Processing..." : "Apply Crop"}
                </button>
                <button
                  onClick={handleCompleteReset}
                  disabled={isProcessing}
                  className="complete-reset-button"
                  title="Reset to original uncropped image"
                >
                  Reset
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

AdvancedCropModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onCropConfirm: PropTypes.func.isRequired,
  currentCropData: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  originalImageData: PropTypes.instanceOf(HTMLImageElement),
  currentRotation: PropTypes.number,
  currentFlipHorizontal: PropTypes.bool,
  currentFlipVertical: PropTypes.bool,
  currentDisplayWidth: PropTypes.number,
  currentDisplayHeight: PropTypes.number,
  onModalRootRef: PropTypes.func,
};

export default AdvancedCropModal;
